/**
 * Marketing Queue — global outbound queue across every workflow + campaign.
 *
 * Source of truth: GET /api/marketing/queue. Returns one row per row of
 * `mkt_sends` whose status is in ('queued','scheduled') and whose
 * scheduled_for is in the future, joined to its parent workflow/campaign,
 * the contact, and the contact's resolved local timezone.
 *
 * UI contract:
 *   • Sort by scheduled_for ASC by default — what's due NEXT shows on top.
 *   • Each row: Recipient · Workflow/Campaign · Step · Subject · Local-time due · UTC due.
 *   • Filter pill row: All · Workflows only · Campaigns only.
 *   • Click any row → deep-link to /marketing/analytics?row=<id> (drawer
 *     opens for that workflow/campaign so the marketer can drill in).
 *   • Refresh button — the queue ticks forward as the cron releases sends.
 *
 * Design call: this page is read-only. Cancelling a queued send is the
 * job of the workflow editor (pause / hard-stop the enrolment) — exposing
 * an inline "cancel send" button here would let a marketer drift workflow
 * state out of sync with what the engine thinks it's queued. Less footgun.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PaperPlaneTilt, ArrowsClockwise, Spinner, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import { formatLocal, asUtc, userTz } from "@/lib/timezone";

const KIND_FILTERS = [
  { id: "all",       label: "All" },
  { id: "workflow",  label: "Workflows" },
  { id: "campaign",  label: "Campaigns" },
];

// "Today only" pill — defaulted ON because in practice marketers
// almost always want to know what's going out in the next 24h before
// they care about long-tail scheduled sends. They can flip it off to
// see the full queue.
function isWithinToday(iso) {
  if (!iso) return false;
  // Normalise the SQL-Server-emitted naive ISO (no Z) before comparing
  // against `new Date()` in the viewer's clock — otherwise the
  // boundary check is off by the local UTC offset (e.g. PT 17:00 row
  // looks like "tomorrow" to a London browser).
  const d = new Date(asUtc(iso));
  if (Number.isNaN(d.getTime())) return false;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return d >= start && d < end;
}

function fmtAbsolute(iso) {
  if (!iso) return "—";
  return formatLocal(iso, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", year: undefined,
    withZoneName: true,
  });
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(asUtc(iso));
  const diffMs = d - new Date();
  if (Number.isNaN(diffMs)) return "";
  const m = Math.round(diffMs / 60_000);
  if (m < -1) return `${Math.abs(m)}m ago`;
  if (Math.abs(m) < 1) return "now";
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  if (Math.abs(h) < 24) return diffMs > 0 ? `in ${h}h` : `${Math.abs(h)}h ago`;
  const d2 = Math.round(h / 24);
  return diffMs > 0 ? `in ${d2}d` : `${Math.abs(d2)}d ago`;
}

export default function MarketingQueuePage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, workflow: 0, campaign: 0 });
  const [filter, setFilter] = useState("all");
  const [todayOnly, setTodayOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/queue?limit=500");
      const list = r.data?.items || [];
      setItems(list);
      setCounts({
        all: list.length,
        workflow: list.filter(x => x.kind === "workflow").length,
        campaign: list.filter(x => x.kind === "campaign").length,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = items;
    if (filter !== "all") rows = rows.filter(x => x.kind === filter);
    if (todayOnly) rows = rows.filter(x => isWithinToday(x.scheduled_for));
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(x =>
        (x.contact_email || "").toLowerCase().includes(q) ||
        (x.parent_name || "").toLowerCase().includes(q) ||
        (x.subject || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [items, filter, search, todayOnly]);

  const todayCount = useMemo(
    () => items.filter(x => isWithinToday(x.scheduled_for)).length,
    [items],
  );

  return (
    <MarketingShell>
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="marketing-queue-page">
        <div className="flex items-center gap-3 mb-1">
          <PaperPlaneTilt size={20} weight="duotone" className="text-[#0055FF]" />
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Outbound Queue
          </h1>
        </div>
        <p className="text-sm text-[#525252] mb-6">
          Every email currently scheduled to go out — across every workflow and campaign — sorted by due date. Updates the moment the queue cron releases a send.
        </p>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {KIND_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`queue-filter-${f.id}`}
              className={`inline-flex items-center gap-2 px-3 h-8 text-xs font-bold uppercase tracking-wider border ${
                filter === f.id
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-[#525252] border-[#E5E5E5] hover:bg-[#FAFAFA]"
              }`}
            >
              {f.label}
              <span className={`text-[10px] tabular-nums ${filter === f.id ? "text-white/70" : "text-[#A3A3A3]"}`}>
                {counts[f.id] ?? 0}
              </span>
            </button>
          ))}
          {/* Today-only chip — defaulted ON. Click to release the
              window so the marketer can see the full forward queue. */}
          <button
            onClick={() => setTodayOnly(v => !v)}
            data-testid="queue-filter-today"
            className={`inline-flex items-center gap-2 px-3 h-8 text-xs font-bold uppercase tracking-wider border ${
              todayOnly
                ? "bg-[#0055FF] text-white border-[#0055FF]"
                : "bg-white text-[#525252] border-[#E5E5E5] hover:bg-[#FAFAFA]"
            }`}
            title={todayOnly ? "Showing only sends due today (recipient-local time). Click to see the full forward queue." : "Click to filter to today's sends only."}
          >
            Today only
            <span className={`text-[10px] tabular-nums ${todayOnly ? "text-white/70" : "text-[#A3A3A3]"}`}>
              {todayCount}
            </span>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter by recipient, workflow, subject…"
                className="border border-[#E5E5E5] pl-7 pr-3 h-8 text-xs w-72 focus:outline-none focus:border-[#0055FF]"
                data-testid="queue-search-input"
              />
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 h-8 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider hover:bg-[#FAFAFA] disabled:opacity-40"
              data-testid="queue-refresh-btn"
            >
              {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />} Refresh
            </button>
          </div>
        </div>

        <div className="border border-[#E5E5E5] bg-white">
          {loading ? (
            <div className="px-5 py-12 text-center text-xs text-[#A3A3A3]" data-testid="queue-loading">
              <Spinner size={20} className="animate-spin mx-auto mb-2" /> Loading queue…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-12 text-center" data-testid="queue-empty">
              <PaperPlaneTilt size={36} weight="duotone" className="mx-auto text-[#A3A3A3] mb-3" />
              <p className="text-sm text-[#525252]">
                {items.length === 0
                  ? "Inbox zero — no emails scheduled to go out."
                  : todayOnly && todayCount === 0
                    ? "Nothing scheduled for today."
                    : "No queued sends match your filters."}
              </p>
              {todayOnly && todayCount === 0 && items.length > 0 && (
                <button
                  onClick={() => setTodayOnly(false)}
                  className="text-xs font-bold uppercase tracking-wider text-[#0055FF] hover:text-[#003ECB] mt-2"
                  data-testid="queue-show-all-btn"
                >
                  Show full forward queue ({items.length})
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="queue-table">
              <thead className="bg-[#FAFAFA] text-[9px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Recipient</th>
                  <th className="text-left px-3 py-2 font-bold">Workflow / Campaign</th>
                  <th className="text-left px-3 py-2 font-bold">Step</th>
                  <th className="text-left px-3 py-2 font-bold">Subject</th>
                  <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Due (recipient local)</th>
                  <th className="text-right px-4 py-2 font-bold whitespace-nowrap">In</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.send_id}
                    onClick={() => navigate(`/marketing/analytics?row=${r.parent_id}`)}
                    className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer"
                    data-testid={`queue-row-${r.send_id}`}
                  >
                    <td className="px-4 py-3 align-top">
                      <p className="font-semibold text-[#0A0A0A] truncate max-w-[200px]" title={r.contact_email}>{r.contact_name || r.contact_email || "—"}</p>
                      {r.contact_name && r.contact_email && (
                        <p className="text-[10px] text-[#A3A3A3] truncate max-w-[200px]">{r.contact_email}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider mr-2 ${r.kind === "workflow" ? "bg-[#EEF2FF] text-[#4338CA]" : "bg-[#F0FDF4] text-[#16A34A]"}`}>{r.kind}</span>
                      <span className="text-[#0A0A0A] font-semibold">{r.parent_name || r.parent_id}</span>
                    </td>
                    <td className="px-3 py-3 align-top text-[#525252] tabular-nums">{r.step_index ?? "—"}</td>
                    <td className="px-3 py-3 align-top truncate max-w-[260px]" title={r.subject || ""}>{r.subject || <span className="text-[#A3A3A3]">—</span>}</td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                      <p className="text-[#0A0A0A] tabular-nums">{fmtAbsolute(r.scheduled_for_local || r.scheduled_for)}</p>
                      {r.timezone && <p className="text-[10px] text-[#A3A3A3]">{r.timezone}</p>}
                    </td>
                    <td className="px-4 py-3 align-top text-right whitespace-nowrap text-[#525252] tabular-nums">
                      {fmtRelative(r.scheduled_for)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </MarketingShell>
  );
}
